// "Today on the farm" — the FARM Home's first answer to "is the farm OK today?"
// (FM11 UF-01). One line above the tiles: a pill per thing that needs someone
// (overdue work, work planned today, open problems), each opening the list it
// counts. The strip is never silent when it does not know.
//
//   loading  → "Checking today…"            (a read is in flight)
//   offline  → "Can't check today offline"  (nothing cached, device paused)
//   failed   → "Couldn't load · Retry"      (nothing held, the read errored)
//   stale    → the figures + "Offline — last known figure, not live."
//   quiet    → "Nothing overdue, planned or open today." — ONLY from reads that
//              answered
//
// The counting rule is the calendar's own (calendar/todayRules.ts), over its own
// horizon read, so opening the calendar next is warm and the numbers agree.
// Every pill is gated by the route gate's own question (useModuleGate): a
// module the person cannot open gets no pill and no read — never a link that
// bounces them back to Home. Three more pills (scouting decisions, certificates
// expiring, machines due) join once the lists they open exist: FM02 UF-07,
// FM10 UF-12, FM08 UF-15.
import { useMemo, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CalendarClock, CalendarDays, ChevronRight, Loader2, TriangleAlert } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { withReadTimeout } from '@/lib/saveTimeout'
import { countOrThrow, stalenessNote } from '@/lib/readState'
import { sastTodayYMD } from '@/lib/dates'
import { POLL } from '@/lib/pollTiers'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { useModuleGate } from '@/hooks/useCanOpenModule'
import WidgetRetry from '@/components/WidgetRetry'
import { AlertPill } from './DashboardTiles'
import { horizonWindow } from './calendar/pageRules'
import { useCalendarHorizon } from './calendar/useCalendarQuery'
import { dueCounts, plannedOnDay, todayStripState, type TodayStrip, type TodayStripState } from './calendar/todayRules'

/** The calendar's Agenda, the list the Overdue and Planned pills count. */
const TODAY_CALENDAR_PATH = '/farm/calendar?view=agenda'
const TODAY_PROBLEMS_PATH = '/farm/problems'

interface ProblemCounts { open: number; pastDue: number }

function useProblemCounts(tenantId: string | null, today: string, enabled: boolean) {
  return useQuery({
    queryKey: ['farm-home-problems', tenantId, today],
    enabled: enabled && !!tenantId,
    refetchInterval: POLL.standard,
    queryFn: async (): Promise<ProblemCounts> => {
      const base = () => supabase.from('farm_problems')
        .select('problem_id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)
        .in('status', ['OPEN', 'IN_PROGRESS'])
      const [open, pastDue] = await Promise.all([
        withReadTimeout(base(), 'home: open problems'),
        withReadTimeout(base().lt('due_on', today), 'home: past-due problems'),
      ])
      return { open: countOrThrow(open), pastDue: countOrThrow(pastDue) }
    },
  })
}

const dayLabel = (ymd: string) =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short' })

interface PillInput {
  wantCalendar: boolean; wantProblems: boolean
  overdue: number; planned: number; problems: ProblemCounts
  today: string; go: (path: string) => void
}

/** One pill per thing with a count above zero, for the pages the person may open. */
function buildPills(i: PillInput): ReactNode[] {
  const out: ReactNode[] = []
  if (i.wantCalendar && i.overdue > 0) {
    out.push(<AlertPill key="overdue" icon={AlertTriangle} tone="red" count={i.overdue} label="overdue"
      onClick={() => i.go(TODAY_CALENDAR_PATH)} />)
  }
  if (i.wantCalendar && i.planned > 0) {
    out.push(<AlertPill key="planned" icon={CalendarClock} tone="blue" count={i.planned} label="planned today"
      onClick={() => i.go(`${TODAY_CALENDAR_PATH}&d=${i.today}`)} />)
  }
  if (i.wantProblems && i.problems.open > 0) {
    out.push(<AlertPill key="problems" icon={TriangleAlert} tone="amber" count={i.problems.open}
      label={i.problems.pastDue > 0 ? `open problems · ${i.problems.pastDue} past due` : 'open problems'}
      onClick={() => i.go(TODAY_PROBLEMS_PATH)} />)
  }
  return out
}

/** The states that carry a sentence instead of figures. */
function StateLine({ state, onRetry }: { state: TodayStripState; onRetry: () => void }) {
  if (state === 'loading') {
    return (
      <p role="status" className="inline-flex items-center gap-1.5 text-sm text-fg-muted">
        <Loader2 size={14} className="animate-spin" /> Checking today…
      </p>
    )
  }
  if (state === 'offline') return <p role="status" className="text-sm text-fg-muted">Can&apos;t check today offline</p>
  if (state === 'failed') return <WidgetRetry onRetry={onRetry} />
  return null
}

function StripBody({ view, pills, onRetry }: { view: TodayStrip; pills: ReactNode[]; onRetry: () => void }) {
  const note = stalenessNote(view.stale)
  const answered = view.state === 'pills' || view.state === 'stale' || view.state === 'quiet'
  return (
    <>
      <StateLine state={view.state} onRetry={onRetry} />
      {answered && pills.length > 0 && <div className="flex flex-wrap gap-2">{pills}</div>}
      {answered && pills.length === 0 && <p className="text-sm text-fg-secondary">Nothing overdue, planned or open today.</p>}
      {view.state === 'stale' && note && <p className="text-[11px] text-muted">{note}</p>}
    </>
  )
}

export default function FarmTodayStrip() {
  const navigate = useNavigate()
  const tenantId = useActiveTenantId()
  const canOpen = useModuleGate()
  const today = sastTodayYMD()

  const wantCalendar = canOpen('farm_calendar') === true
  const wantProblems = canOpen('farm_problems') === true

  const horizon = useMemo(() => horizonWindow(today), [today])
  const horizonQ = useCalendarHorizon(horizon.from, horizon.to, wantCalendar)
  const problemsQ = useProblemCounts(tenantId, today, wantProblems)

  // Only the reads this person can use count toward the strip's state: a
  // switched-off query sits at "loading" forever and would hold the strip there.
  const reads = [
    ...(wantCalendar ? [horizonQ] : []),
    ...(wantProblems ? [problemsQ] : []),
  ]
  // Nothing this person may open (or the gate is still deciding): say nothing
  // rather than "nothing today" about a question never asked.
  if (reads.length === 0) return null

  const pills = buildPills({
    wantCalendar, wantProblems,
    overdue: dueCounts(horizonQ.data ?? []).overdue,
    planned: plannedOnDay(horizonQ.data ?? [], today),
    problems: problemsQ.data ?? { open: 0, pastDue: 0 },
    today, go: navigate,
  })
  const view = todayStripState(reads, pills.length)
  const retry = () => {
    if (wantCalendar) void horizonQ.refetch()
    if (wantProblems) void problemsQ.refetch()
  }

  return (
    <section aria-label="Today on the farm" className="mb-4 surface rounded-2xl p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[11px] uppercase tracking-wider text-muted font-bold">
          Today · {dayLabel(today)}
        </h2>
        {wantCalendar && (
          <button type="button" onClick={() => navigate(TODAY_CALENDAR_PATH)}
            className="inline-flex items-center gap-0.5 text-xs text-fg-muted hover:text-fg min-h-[44px] px-1">
            <CalendarDays size={14} /> Calendar <ChevronRight size={14} />
          </button>
        )}
      </div>
      <StripBody view={view} pills={pills} onRetry={retry} />
    </section>
  )
}
